import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { meApi } from "../../api";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "../layout/Sidebar";
import classes from "./Progress.module.scss";

type Measurement = {
  id: string; date: string;
  weight?: number; bodyFatPct?: number; musclePct?: number;
  waterPct?: number; fatClipper?: number;
};

const renderProgressChart = (
  label: string,
  pts: { date: string; val: number }[],
  color: string,
  unit: string,
  gradId: string,
) => {
  if (pts.length < 2) return null;
  const max   = Math.max(...pts.map(p => p.val));
  const min   = Math.min(...pts.map(p => p.val));
  const range = max - min || 1;
  const W = 560, H = 180, pad = 32;
  const xStep = (W - pad * 2) / Math.max(pts.length - 1, 1);
  const yCoord = (v: number) => pad + (1 - (v - min) / range) * (H - pad * 2);
  const xCoord = (i: number) => pad + i * xStep;
  const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${xCoord(i)} ${yCoord(p.val)}`).join(" ");
  const areaD = `${pathD} L ${xCoord(pts.length - 1)} ${H - pad} L ${xCoord(0)} ${H - pad} Z`;
  return (
    <div key={label} className={classes.chart_card}>
      <div className={classes.chart_header}>
        <h3 className={classes.chart_title}>{label}</h3>
        <span className={classes.chart_unit}>{unit.trim()}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className={classes.chart_svg}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity="0.2"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={areaD} fill={`url(#${gradId})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={xCoord(i)} cy={yCoord(p.val)} r="4.5" fill={color} stroke="white" strokeWidth="2"/>
            <text x={xCoord(i)} y={yCoord(p.val) - 11} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">{p.val}{unit}</text>
            <text x={xCoord(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#7A8C7A">{p.date}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const Progress = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    meApi.getMeasurements()
      .then(d => setMeasurements(d?.measurements ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const sorted      = [...measurements].reverse();
  const weightPts   = sorted.filter(m => m.weight     != null).map(m => ({ date: m.date, val: m.weight!     }));
  const bodyFatPts  = sorted.filter(m => m.bodyFatPct != null).map(m => ({ date: m.date, val: m.bodyFatPct! }));
  const musclePts   = sorted.filter(m => m.musclePct  != null).map(m => ({ date: m.date, val: m.musclePct!  }));
  const waterPts    = sorted.filter(m => m.waterPct   != null).map(m => ({ date: m.date, val: m.waterPct!   }));
  const fatClipPts  = sorted.filter(m => m.fatClipper != null).map(m => ({ date: m.date, val: m.fatClipper! }));

  const charts = [
    renderProgressChart("Weight",      weightPts,  "#4A7C40", " kg", "pg_wg"),
    renderProgressChart("Body Fat",    bodyFatPts, "#E05C5C", "%",   "pg_bf"),
    renderProgressChart("Muscle",      musclePts,  "#3B7CC9", "%",   "pg_mg"),
    renderProgressChart("Water",       waterPts,   "#2196B0", "%",   "pg_wtr"),
    renderProgressChart("Fat Clipper", fatClipPts, "#C97A3B", " mm", "pg_fc"),
  ].filter(Boolean);

  return (
    <div className={classes.layout}>
      <Sidebar />
      <div className={classes.main_wrap}>
        <header className={classes.topbar}>
          <div className={classes.breadcrumb}>
            <button type="button" className={classes.breadcrumb_btn} onClick={() => navigate("/dashboard")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              Dashboard
            </button>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className={classes.breadcrumb_current}>Progress</span>
          </div>
          <div className={classes.topbar_right}>
            <div className={classes.avatar}>{(user?.name ?? "?")[0].toUpperCase()}</div>
          </div>
        </header>
        <main className={classes.content}>
          {loading ? (
            <div className={classes.spinner_wrap}>
              <div className={classes.spinner} />
            </div>
          ) : charts.length === 0 ? (
            <div className={classes.empty}>
              <p>No progress data yet.</p>
              <p>Your dietitian needs to add at least 2 measurement records to see charts here.</p>
            </div>
          ) : (
            <div className={classes.charts_grid}>
              {charts}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Progress;
